/**
 * 路由配置 - 11 个核心页面 × 3 语言 = 33 个 URL
 *
 * 设计原则：
 * - 中文（默认）URL 无前缀：/、/about、/x5 等（默认就是中文）
 * - 英文 alt 路由：/en/、/en/about、/en/x5 等
 * - 越南文 alt 路由：/vi/、/vi/about、/vi/x5 等
 * - 路径前缀决定 i18n 语言，进入路由时同步 i18n.changeLanguage
 *
 * 此文件被 App.js 引用，被 react-snap 预渲染使用，被 sitemap 脚本使用
 */

import React, { useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Home from '../components/home';
import About from '../components/about';
import Capacity from '../components/capacity';
import Advantage from '../components/advantage';
import Products from '../components/products';
import Contact from '../components/contact';
import X5Machine from '../components/products/x5';
import X6Machine from '../components/products/x6';
import ZMMachine from '../components/products/zm';
import ChooseSpreadingMachine from '../components/purchaseGuide';
import SpreadersVideo from '../components/spreadersVideo';
import Autumn2026 from '../components/autumn';

import { LANGS, LANG_FROM_PATH } from '../config/siteConfig';

// 11 个核心页面 slug（顺序对应 sitemap 优先级）
const PAGES = [
  { slug: '', element: Home, name: 'home' },
  { slug: 'about', element: About, name: 'about' },
  { slug: 'capacity', element: Capacity, name: 'capacity' },
  { slug: 'advantage', element: Advantage, name: 'advantage' },
  { slug: 'products', element: Products, name: 'products' },
  { slug: 'x5', element: X5Machine, name: 'x5' },
  { slug: 'x6', element: X6Machine, name: 'x6' },
  { slug: 'zm', element: ZMMachine, name: 'zm' },
  { slug: 'spreaders_video', element: SpreadersVideo, name: 'spreaders_video' },
  { slug: 'purchase_guide', element: ChooseSpreadingMachine, name: 'purchase_guide' },
  { slug: 'contact', element: Contact, name: 'contact' },
  // 2026 秋季推文（中英双语，内容取自对外发布版 PDF）
  { slug: 'autumn-2026', element: Autumn2026, name: 'autumn' },
];

/**
 * 路由表生成器
 * @returns {Array<{path, element, lang, pageName}>}
 */
export const buildRouteList = () => {
  const routes = [];

  for (const page of PAGES) {
    // 中文（默认）- 无前缀
    routes.push({
      path: page.slug ? `/${page.slug}` : '/',
      element: React.createElement(page.element),
      lang: 'zh',
      pageName: page.name,
    });

    // 英文 alt - /en/ 前缀
    routes.push({
      path: page.slug ? `/en/${page.slug}` : '/en/',
      element: React.createElement(page.element),
      lang: 'en',
      pageName: page.name,
    });

    // 中文 - /zh/ 前缀
    routes.push({
      path: page.slug ? `/zh/${page.slug}` : '/zh/',
      element: React.createElement(page.element),
      lang: 'zh',
      pageName: page.name,
    });

    // 越南语 - /vi/ 前缀
    routes.push({
      path: page.slug ? `/vi/${page.slug}` : '/vi/',
      element: React.createElement(page.element),
      lang: 'vi',
      pageName: page.name,
    });
  }

  return routes;
};

/**
 * LangRoute - 根据当前路径切换 i18n 语言
 * 进入路由时同步设置 i18n.changeLanguage 和 localStorage
 *
 * 注意：i18n.changeLanguage 会触发组件层的 languageChanged 事件，
 * 现有 i18n 翻译会自然切换。但**不会**触发 location.reload()，
 * 这点与原 App.js 的语言切换按钮行为不同（按钮仍需 reload）。
 */
const LangRoute = ({ element, lang }) => {
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    // 路径决定的语言优先级最高（即使是默认英文）
    const pathLang = LANG_FROM_PATH(location.pathname);
    const targetLang = pathLang || lang;

    if (i18n.language !== targetLang) {
      // 不调用 reload 的方式：直接设置 i18n 状态
      i18n.changeLanguage(targetLang);
    }

    // 同步 localStorage，供用户主动切换按钮使用
    try {
      localStorage.setItem('lang', targetLang);
    } catch (_) { /* SSR / 隐私模式忽略 */ }

    // 同步 <html lang>
    document.documentElement.lang = targetLang;
  }, [location.pathname, lang, i18n]);

  return element;
};

/**
 * 路由渲染 - 包装 Routes + Route，每条路由包 LangRoute
 */
const AppRoutes = () => {
  const routeList = buildRouteList();

  return (
    <Routes>
      {routeList.map((r, idx) => (
        <Route
          key={`${r.lang}-${r.path}-${idx}`}
          path={r.path}
          element={<LangRoute element={r.element} lang={r.lang} />}
        />
      ))}
      {/* 兜底：未知路径重定向到首页 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;
export { PAGES, LANGS };
